import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon } from "lucide-react";
import Link from "next/link";

import type { INavbarLink } from "./Navbar";
import { useLazyIntercom } from "~/hooks";

interface NavbarMenuProps {
	link: INavbarLink;
	isInverted?: boolean;
}

const NavbarLink = ({ link, isInverted }: NavbarMenuProps) => {
	const { preload, show } = useLazyIntercom();
	return link.children ? (
		<div className="relative">
			<Menu>
				{({ open }) => (
					<div>
						<MenuButton
							className={clsx(
								"flex items-center gap-0.5 rounded-full px-2 tablet:px-3 py-1.5 font-medium text-black hover:bg-sky-50/50 dark:text-white dark:hover:bg-sky-500/5",
								{
									"bg-sky-50 dark:bg-sky-500/10": open && !isInverted,
									"text-white hover:bg-slate-50/10": isInverted,
									"bg-slate-50/10": open && isInverted,
								},
							)}
						>
							{link.label}
							<ChevronDownIcon
								className={clsx("-mr-1 transition-transform duration-300", {
									"rotate-180": open,
								})}
								size={20}
								strokeWidth={2.2}
							/>
						</MenuButton>

						<AnimatePresence mode="wait">
							{open && (
								<MenuItems
									static
									as={motion.div}
									initial={{ opacity: 0, y: -5 }}
									animate={{
										opacity: 1,
										y: 0,
										transition: {
											duration: 0.2,
										},
									}}
									exit={{
										opacity: 0,
										y: -5,
										transition: {
											duration: 0.15,
										},
									}}
									className="absolute top-12 z-20 mt-0.5 flex flex-col gap-1 rounded-xl border bg-white p-2 shadow-black/5 shadow-xl dark:bg-slate-800"
								>
									{link.children?.map(
										({
											href,
											label,
											icon: Icon,
											target,
											className,
											intercomButton,
										}) => (
											<MenuItem key={href}>
												{href ? (
													<Link
														href={href}
														target={target}
														className="flex min-w-40 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 font-medium transition-colors hover:bg-sky-50 dark:hover:bg-sky-300/10"
													>
														<Icon size={20} />
														{label}
													</Link>
												) : intercomButton ? (
													<button
														onMouseEnter={preload}
														onFocus={preload}
														onClick={() => show()}
														className={clsx(
															className,
															"flex min-w-40 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 font-medium transition-colors hover:bg-sky-50 dark:hover:bg-sky-300/10",
														)}
													>
														<Icon size={20} />
														{label}
													</button>
												) : (
													<button
														className={clsx(
															className,
															"flex min-w-40 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 font-medium transition-colors hover:bg-sky-50 dark:hover:bg-sky-300/10",
														)}
													>
														<Icon size={20} />
														{label}
													</button>
												)}
											</MenuItem>
										),
									)}
								</MenuItems>
							)}
						</AnimatePresence>
					</div>
				)}
			</Menu>
		</div>
	) : link.href ? (
		<Link
			target={link.target}
			href={link.href}
			className={clsx(
				"flex items-center gap-1 rounded-full px-2 tablet:px-3 py-1.5 font-medium text-black hover:bg-sky-50/50 dark:text-white dark:hover:bg-sky-500/5",
				link.className,
				{ "text-white hover:bg-slate-50/10": isInverted },
			)}
		>
			{link.label}{" "}
		</Link>
	) : (
		<button
			onClick={link.onClick}
			className="flex items-center gap-0.5 rounded-full px-2 tablet:px-3 py-1.5 font-medium text-black hover:bg-sky-50/50 dark:text-white dark:hover:bg-sky-500/5"
		>
			{link.label}
		</button>
	);
};

export default NavbarLink;
